import { REPORT_DATE_MODE, getCurrentReportDateFilters } from '@/lib/report-date-range'
import dayjs from '@/lib/dayjs'
import type { Dayjs } from 'dayjs'
import { z } from 'zod/v4'
import {
  floristStatusSchema,
  type FloristStatusFilter,
} from '@/lib/services/organizations/organization.schema'

const dateStringSchema = z.iso.date('Invalid date')
const reportDateModeSchema = z.enum([
  REPORT_DATE_MODE.DAY,
  REPORT_DATE_MODE.WEEK,
  REPORT_DATE_MODE.CUSTOM,
])
const reportPaginationSearchSchema = {
  page: z.coerce.number().int().positive().optional(),
  pageSize: z.coerce.number().int().positive().optional(),
  page_size: z.coerce.number().int().positive().optional(),
}
const fulfillmentTypeSchema = z.enum(['delivery', 'pickup', 'in-store'])
const sourceSchema = z.enum(['web', 'pos'])
const cashDrawerSortSchema = z.enum(['newest', 'oldest', 'amount_desc', 'amount_asc'])
export const FLORIST_PERFORMANCE_VIEW = {
  ORDERS: 'orders',
  CUSTOMER_SALES: 'customer_sales',
  PLATFORM_REVENUE: 'platform_revenue',
  MOMENTUM: 'momentum',
} as const
export const FLORIST_PERFORMANCE_STATUS = {
  LIVE: floristStatusSchema.enum.live,
  NEEDS_SETUP: floristStatusSchema.enum.needs_setup,
  LOST: floristStatusSchema.enum.lost,
  ALL: 'all',
} as const
export const FLORIST_PERFORMANCE_MOMENTUM_METRIC = {
  ORDERS: 'orders',
  CUSTOMER_SALES: 'customer_sales',
  PLATFORM_REVENUE: 'platform_revenue',
} as const
export const FLORIST_PERFORMANCE_SORT = {
  FLORIST_ASC: 'florist_asc',
  FLORIST_DESC: 'florist_desc',
  STATUS_ASC: 'status_asc',
  STATUS_DESC: 'status_desc',
  GO_LIVE_AT_ASC: 'go_live_at_asc',
  GO_LIVE_AT_DESC: 'go_live_at_desc',
  WEB_ASC: 'web_asc',
  WEB_DESC: 'web_desc',
  SUBSCRIPTION_ASC: 'subscription_asc',
  SUBSCRIPTION_DESC: 'subscription_desc',
  POS_ASC: 'pos_asc',
  POS_DESC: 'pos_desc',
  CONDOLENCES_ASC: 'condolences_asc',
  CONDOLENCES_DESC: 'condolences_desc',
  FSN_ASC: 'fsn_asc',
  FSN_DESC: 'fsn_desc',
  OTHER_ASC: 'other_asc',
  OTHER_DESC: 'other_desc',
  MERCHANT_FEE_MARGIN_ASC: 'merchant_fee_margin_asc',
  MERCHANT_FEE_MARGIN_DESC: 'merchant_fee_margin_desc',
  TOTAL_ASC: 'total_asc',
  TOTAL_DESC: 'total_desc',
  CURRENT_7_ASC: 'current_7_asc',
  CURRENT_7_DESC: 'current_7_desc',
  PRIOR_7_ASC: 'prior_7_asc',
  PRIOR_7_DESC: 'prior_7_desc',
  CHANGE_7_ASC: 'change_7_asc',
  CHANGE_7_DESC: 'change_7_desc',
  CURRENT_30_ASC: 'current_30_asc',
  CURRENT_30_DESC: 'current_30_desc',
  PRIOR_30_ASC: 'prior_30_asc',
  PRIOR_30_DESC: 'prior_30_desc',
  CHANGE_30_ASC: 'change_30_asc',
  CHANGE_30_DESC: 'change_30_desc',
} as const
const floristPerformanceSortSchema = z.enum(Object.values(FLORIST_PERFORMANCE_SORT))
const floristPerformanceViewSchema = z.enum(Object.values(FLORIST_PERFORMANCE_VIEW))
const floristPerformanceStatusSchema = z.enum(Object.values(FLORIST_PERFORMANCE_STATUS))
const floristPerformanceMomentumMetricSchema = z.enum(
  Object.values(FLORIST_PERFORMANCE_MOMENTUM_METRIC)
)
export const SALES_SUMMARY_GRANULARITY = {
  HOURLY: 'hourly',
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
} as const
export const SALES_SUMMARY_TRANSACTION_SORT = {
  ORGANIZATION_ASC: 'organization_asc',
  ORGANIZATION_DESC: 'organization_desc',
  TRANSACTION_ASC: 'transaction_asc',
  TRANSACTION_DESC: 'transaction_desc',
  SALES_ORDER_ASC: 'sales_order_asc',
  SALES_ORDER_DESC: 'sales_order_desc',
  CUSTOMER_ASC: 'customer_asc',
  CUSTOMER_DESC: 'customer_desc',
  SOURCE_ASC: 'source_asc',
  SOURCE_DESC: 'source_desc',
  FULFILLMENT_ASC: 'fulfillment_asc',
  FULFILLMENT_DESC: 'fulfillment_desc',
  PAYOUT_ASC: 'payout_asc',
  PAYOUT_DESC: 'payout_desc',
  TOTAL_ASC: 'total_asc',
  TOTAL_DESC: 'total_desc',
  CREATED_ASC: 'created_asc',
  CREATED_DESC: 'created_desc',
} as const

type SalesSummaryGranularity =
  (typeof SALES_SUMMARY_GRANULARITY)[keyof typeof SALES_SUMMARY_GRANULARITY]

type SalesSummaryGranularityOption = {
  value: SalesSummaryGranularity
  label: string
  unavailableLabel: string | null
}

export const SALES_SUMMARY_GRANULARITY_OPTIONS: SalesSummaryGranularityOption[] = [
  {
    value: SALES_SUMMARY_GRANULARITY.HOURLY,
    label: 'Hour',
    unavailableLabel: '7 days or less',
  },
  {
    value: SALES_SUMMARY_GRANULARITY.DAILY,
    label: 'Day',
    unavailableLabel: '6 months or less',
  },
  {
    value: SALES_SUMMARY_GRANULARITY.WEEKLY,
    label: 'Week',
    unavailableLabel: '3 years or less',
  },
  {
    value: SALES_SUMMARY_GRANULARITY.MONTHLY,
    label: 'Month',
    unavailableLabel: null,
  },
]
const salesSummaryGranularitySchema = z.enum(
  SALES_SUMMARY_GRANULARITY_OPTIONS.map((option) => option.value)
)
const phoneClickGranularitySchema = z.enum([
  SALES_SUMMARY_GRANULARITY.DAILY,
  SALES_SUMMARY_GRANULARITY.WEEKLY,
  SALES_SUMMARY_GRANULARITY.MONTHLY,
])
const phoneClickReportPageSizeSchema = z.coerce.number().int().positive().max(100).optional()
export const PHONE_CLICK_REPORT_SORT = {
  DATE_ASC: 'date_asc',
  DATE_DESC: 'date_desc',
  PHONE_ASC: 'phone_asc',
  PHONE_DESC: 'phone_desc',
  CLICKS_ASC: 'clicks_asc',
  CLICKS_DESC: 'clicks_desc',
} as const
const phoneClickReportSortSchema = z.enum(Object.values(PHONE_CLICK_REPORT_SORT))
const salesSummaryTransactionSortSchema = z.enum(Object.values(SALES_SUMMARY_TRANSACTION_SORT))
const SALES_SUMMARY_HOURLY_MAX_DAYS = 7
const SALES_SUMMARY_DAILY_MAX_DAYS = 183
const SALES_SUMMARY_WEEKLY_MAX_DAYS = 1096
const dateRangeError = {
  message: 'Start date must be on or before end date',
  path: ['end_date'],
}
const optionalDateRangeError = {
  message: 'Choose a valid start and end date',
  path: ['end_date'],
}
const phoneClickDateRangeError = {
  message: 'Choose a date range of 366 days or fewer',
  path: ['end_date'],
}
export const PHONE_CLICK_REPORT_MAX_RANGE_DAYS = 366

type ReportDateMode = z.infer<typeof reportDateModeSchema>
export type PhoneClickGranularity = z.infer<typeof phoneClickGranularitySchema>

type ReportDateRange = {
  start_date?: string
  end_date?: string
}

type RequiredReportDateRange = {
  start_date: string
  end_date: string
}

function hasCompleteDateRange({ start_date, end_date }: ReportDateRange) {
  if (!start_date && !end_date) {
    return true
  }

  if (!start_date || !end_date) {
    return false
  }

  return start_date <= end_date
}

function hasOrderedRequiredDateRange({ start_date, end_date }: RequiredReportDateRange) {
  return start_date <= end_date
}

function isWithinPhoneClickReportRange({ start_date, end_date }: ReportDateRange) {
  if (!start_date || !end_date || start_date > end_date) {
    return true
  }

  return dayjs(end_date).diff(dayjs(start_date), 'day') < PHONE_CLICK_REPORT_MAX_RANGE_DAYS
}

// Report schemas extend these bases: `.extend()` keeps the date keys first and keeps the range
// refinement. Zod throws if an extra shape redeclares a date key on a refined object.
const requiredDateRangeSchema = z
  .object({
    start_date: dateStringSchema,
    end_date: dateStringSchema,
  })
  .refine(hasOrderedRequiredDateRange, dateRangeError)

const optionalDateRangeSearchSchema = z
  .object({
    start_date: dateStringSchema.optional(),
    end_date: dateStringSchema.optional(),
  })
  .refine(hasCompleteDateRange, optionalDateRangeError)

function buildRequiredDateRangeSchema<TShape extends z.ZodRawShape>(shape: TShape) {
  return requiredDateRangeSchema.extend(shape)
}

function buildOptionalDateRangeSearchSchema<TShape extends z.ZodRawShape>(shape: TShape) {
  return optionalDateRangeSearchSchema.extend(shape)
}

function hasExplicitDateRange<TSearch extends ReportDateRange>(
  parsedSearch: TSearch
): parsedSearch is TSearch & RequiredReportDateRange {
  return Boolean(parsedSearch.start_date && parsedSearch.end_date)
}

export const salesTaxReportFiltersSchema = buildRequiredDateRangeSchema({
  date_mode: reportDateModeSchema.optional(),
})

export const salesTaxReportSearchSchema = buildOptionalDateRangeSearchSchema({
  date_mode: reportDateModeSchema.optional(),
  ...reportPaginationSearchSchema,
})

export type SalesTaxReportFilters = z.infer<typeof salesTaxReportFiltersSchema>
export type SalesTaxReportSearch = z.infer<typeof salesTaxReportSearchSchema>

export function getDefaultSalesTaxReportFilters(
  defaultFilters: SalesTaxReportFilters = getCurrentReportDateFilters()
): SalesTaxReportFilters {
  return salesTaxReportFiltersSchema.parse(defaultFilters)
}

export function getSalesTaxReportFiltersFromSearch(
  search: unknown,
  defaultFilters: SalesTaxReportFilters
): SalesTaxReportFilters {
  const parsedSearch = salesTaxReportSearchSchema.parse(search)

  if (!hasExplicitDateRange(parsedSearch)) {
    return getDefaultSalesTaxReportFilters(defaultFilters)
  }

  return salesTaxReportFiltersSchema.parse({
    ...parsedSearch,
    date_mode: parsedSearch.date_mode ?? defaultFilters?.date_mode ?? REPORT_DATE_MODE.CUSTOM,
  })
}

export const accountingJournalReportFiltersSchema = buildRequiredDateRangeSchema({
  date_mode: reportDateModeSchema.optional(),
})

export const accountingJournalReportSearchSchema = buildOptionalDateRangeSearchSchema({
  date_mode: reportDateModeSchema.optional(),
  ...reportPaginationSearchSchema,
})

export type AccountingJournalReportFilters = z.infer<typeof accountingJournalReportFiltersSchema>
export type AccountingJournalReportSearch = z.infer<typeof accountingJournalReportSearchSchema>

export function getAccountingJournalReportFiltersFromSearch(
  search: unknown,
  defaultFilters: AccountingJournalReportFilters
): AccountingJournalReportFilters {
  const parsedSearch = accountingJournalReportSearchSchema.parse(search)

  if (!hasExplicitDateRange(parsedSearch)) {
    return accountingJournalReportFiltersSchema.parse(defaultFilters)
  }

  return accountingJournalReportFiltersSchema.parse({
    ...parsedSearch,
    date_mode: parsedSearch.date_mode ?? defaultFilters?.date_mode ?? REPORT_DATE_MODE.CUSTOM,
  })
}

export const closedDaysReportFiltersSchema = buildRequiredDateRangeSchema({
  date_mode: reportDateModeSchema.optional(),
})

export const closedDaysReportSearchSchema = buildOptionalDateRangeSearchSchema({
  date_mode: reportDateModeSchema.optional(),
})

export type ClosedDaysReportFilters = z.infer<typeof closedDaysReportFiltersSchema>
export type ClosedDaysReportSearch = z.infer<typeof closedDaysReportSearchSchema>

export function getClosedDaysReportFiltersFromSearch(
  search: unknown,
  defaultFilters: ClosedDaysReportFilters
): ClosedDaysReportFilters {
  const parsedSearch = closedDaysReportSearchSchema.parse(search)

  if (!hasExplicitDateRange(parsedSearch)) {
    return closedDaysReportFiltersSchema.parse(defaultFilters)
  }

  return closedDaysReportFiltersSchema.parse({
    ...parsedSearch,
    date_mode: parsedSearch.date_mode ?? defaultFilters?.date_mode ?? REPORT_DATE_MODE.CUSTOM,
  })
}

export const phoneClickReportFiltersSchema = buildRequiredDateRangeSchema({
  date_mode: reportDateModeSchema.optional(),
  sort: phoneClickReportSortSchema.default(PHONE_CLICK_REPORT_SORT.DATE_DESC),
  page: z.coerce.number().int().positive().optional(),
  page_size: phoneClickReportPageSizeSchema,
}).refine(isWithinPhoneClickReportRange, phoneClickDateRangeError)

export const phoneClickReportSearchSchema = buildOptionalDateRangeSearchSchema({
  date_mode: reportDateModeSchema.optional(),
  sort: phoneClickReportSortSchema.optional(),
  granularity: phoneClickGranularitySchema.optional(),
  ...reportPaginationSearchSchema,
  page_size: phoneClickReportPageSizeSchema,
}).refine(isWithinPhoneClickReportRange, phoneClickDateRangeError)

export type PhoneClickReportFilters = z.infer<typeof phoneClickReportFiltersSchema>
export type PhoneClickReportSearch = z.infer<typeof phoneClickReportSearchSchema>

// Callers pass a date range; sort falls back to the schema default.
type PhoneClickReportDefaultFilters = Omit<PhoneClickReportFilters, 'sort'> & {
  sort?: PhoneClickReportFilters['sort']
}

export function getDefaultPhoneClickReportFilters(
  defaultFilters: PhoneClickReportDefaultFilters = getCurrentReportDateFilters()
): PhoneClickReportFilters {
  return phoneClickReportFiltersSchema.parse({
    ...defaultFilters,
    page: defaultFilters?.page ?? 1,
    page_size: defaultFilters?.page_size ?? 7,
  })
}

export function getPhoneClickReportFiltersFromSearch(
  search: unknown,
  defaultFilters: PhoneClickReportDefaultFilters
): PhoneClickReportFilters {
  const parsedSearch = phoneClickReportSearchSchema.parse(search)
  const tableFilters = {
    sort: parsedSearch.sort ?? defaultFilters?.sort ?? PHONE_CLICK_REPORT_SORT.DATE_DESC,
    page: parsedSearch.page ?? defaultFilters?.page ?? 1,
    page_size: parsedSearch.page_size ?? defaultFilters?.page_size ?? 7,
  }

  if (!hasExplicitDateRange(parsedSearch)) {
    return getDefaultPhoneClickReportFilters({
      ...defaultFilters,
      ...tableFilters,
    })
  }

  return phoneClickReportFiltersSchema.parse({
    ...parsedSearch,
    ...tableFilters,
    date_mode: parsedSearch.date_mode ?? defaultFilters?.date_mode ?? REPORT_DATE_MODE.CUSTOM,
  })
}

export function getPhoneClickGranularityOptionsForRange(range: ReportDateRange) {
  return getSalesSummaryGranularityOptionsForRange(range).filter(
    (option) => option.value !== SALES_SUMMARY_GRANULARITY.HOURLY
  )
}

/** The granularity a group-by select value names, or undefined when it names none. */
export function parsePhoneClickGranularity(value: string): PhoneClickGranularity | undefined {
  const result = phoneClickGranularitySchema.safeParse(value)

  return result.success ? result.data : undefined
}

export function resolvePhoneClickGranularityForRange(
  granularity: SalesSummaryGranularity | undefined,
  range: ReportDateRange
): PhoneClickGranularity {
  const resolvedGranularity = resolveSalesSummaryGranularityForRange(granularity, range)

  return resolvedGranularity === SALES_SUMMARY_GRANULARITY.HOURLY
    ? SALES_SUMMARY_GRANULARITY.DAILY
    : resolvedGranularity
}

export const customerBalanceReportFiltersSchema = z.object({
  as_of_date: dateStringSchema,
})

export const customerBalanceReportSearchSchema = z.object({
  as_of_date: dateStringSchema.optional(),
  search: z.string().optional().catch(undefined),
})

export type CustomerBalanceReportFilters = z.infer<typeof customerBalanceReportFiltersSchema>
export type CustomerBalanceReportSearch = z.infer<typeof customerBalanceReportSearchSchema>

export function getCustomerBalanceReportFiltersFromSearch(
  search: unknown,
  defaultFilters: CustomerBalanceReportFilters
): CustomerBalanceReportFilters {
  const parsedSearch = customerBalanceReportSearchSchema.parse(search)

  return customerBalanceReportFiltersSchema.parse({
    as_of_date: parsedSearch.as_of_date ?? defaultFilters?.as_of_date,
  })
}

export function getCustomerBalanceReportSearchParamsFromSearch(
  search: unknown,
  defaultFilters: CustomerBalanceReportFilters
) {
  const parsedSearch = customerBalanceReportSearchSchema.parse(search)
  const trimmedSearch = parsedSearch.search?.trim()

  return {
    as_of_date: parsedSearch.as_of_date ?? defaultFilters?.as_of_date,
    ...(trimmedSearch ? { search: trimmedSearch } : {}),
  }
}

export const salesSummaryReportFiltersSchema = buildRequiredDateRangeSchema({
  date_mode: reportDateModeSchema.optional(),
  fulfillment_type: fulfillmentTypeSchema.nullable().optional(),
  source: sourceSchema.nullable().optional(),
  granularity: salesSummaryGranularitySchema.optional(),
  search: z.string().nullable().optional(),
  sort: salesSummaryTransactionSortSchema.nullable().optional(),
  page: z.coerce.number().int().positive().optional(),
  page_size: z.coerce.number().int().positive().optional(),
})

export type SalesSummaryReportFilters = z.infer<typeof salesSummaryReportFiltersSchema>

export const cashDrawerReportSearchSchema = buildOptionalDateRangeSearchSchema({
  date_mode: reportDateModeSchema.optional(),
  location_id: z.coerce.number().int().positive().optional(),
  workstation_id: z.coerce.number().int().positive().optional(),
  search: z.string().optional().catch(undefined),
  sort: cashDrawerSortSchema.optional(),
  ...reportPaginationSearchSchema,
})

export type CashDrawerReportSearch = z.infer<typeof cashDrawerReportSearchSchema>

export type CashDrawerReportFilters = {
  start_date: string
  end_date: string
  date_mode: ReportDateMode
  location_id?: number
  workstation_id?: number
  search?: string
  sort: z.infer<typeof cashDrawerSortSchema>
  page: number
  page_size: number
}

type CashDrawerReportDefaultFilters = RequiredReportDateRange & {
  date_mode?: ReportDateMode
}

export function getCashDrawerReportFiltersFromSearch(
  search: unknown,
  defaultFilters: CashDrawerReportDefaultFilters
): CashDrawerReportFilters {
  const parsed = cashDrawerReportSearchSchema.parse(search)

  return {
    start_date: parsed.start_date ?? defaultFilters.start_date,
    end_date: parsed.end_date ?? defaultFilters.end_date,
    date_mode: parsed.date_mode ?? defaultFilters.date_mode ?? REPORT_DATE_MODE.CUSTOM,
    ...(parsed.location_id ? { location_id: parsed.location_id } : {}),
    ...(parsed.workstation_id ? { workstation_id: parsed.workstation_id } : {}),
    ...(parsed.search ? { search: parsed.search } : {}),
    sort: parsed.sort ?? 'newest',
    page: parsed.page ?? 1,
    page_size: parsed.page_size ?? parsed.pageSize ?? 20,
  }
}

export function getCashDrawerReportQueryFilters(
  filters: CashDrawerReportFilters
): CashDrawerReportFilters {
  const { search, ...queryFilters } = filters
  const trimmedSearch = search?.trim()

  return trimmedSearch ? { ...queryFilters, search: trimmedSearch } : queryFilters
}

export function mergeCashDrawerReportFilters(
  filters: CashDrawerReportFilters,
  changes: Partial<CashDrawerReportFilters>
): CashDrawerReportFilters {
  return { ...filters, ...changes, page: changes.page ?? 1 }
}

export const salesSummaryReportSearchSchema = buildOptionalDateRangeSearchSchema({
  date_mode: reportDateModeSchema.optional(),
  fulfillment_type: fulfillmentTypeSchema.optional(),
  source: sourceSchema.optional(),
  granularity: salesSummaryGranularitySchema.optional(),
  search: z.string().optional().catch(undefined),
  sort: salesSummaryTransactionSortSchema.optional(),
  ...reportPaginationSearchSchema,
})

export type SalesSummaryReportSearch = z.infer<typeof salesSummaryReportSearchSchema>

export function getSalesSummaryReportFiltersFromSearch(
  search: unknown,
  defaultFilters: SalesSummaryReportFilters
): SalesSummaryReportFilters {
  const parsedSearch = salesSummaryReportSearchSchema.parse(search)
  const sharedFilters = {
    date_mode: parsedSearch.date_mode ?? defaultFilters?.date_mode ?? REPORT_DATE_MODE.CUSTOM,
    fulfillment_type: parsedSearch.fulfillment_type ?? null,
    source: parsedSearch.source ?? null,
    search: parsedSearch.search?.trim() || null,
    sort: parsedSearch.sort ?? defaultFilters?.sort ?? null,
    page: parsedSearch.page ?? defaultFilters?.page ?? 1,
    page_size: parsedSearch.page_size ?? parsedSearch.pageSize ?? defaultFilters?.page_size ?? 20,
  }
  const rangeFilters = hasExplicitDateRange(parsedSearch)
    ? parsedSearch
    : {
        ...defaultFilters,
        ...sharedFilters,
      }
  const granularity = resolveSalesSummaryGranularityForRange(
    parsedSearch.granularity ?? defaultFilters?.granularity,
    rangeFilters
  )

  if (!hasExplicitDateRange(parsedSearch)) {
    return salesSummaryReportFiltersSchema.parse({
      ...defaultFilters,
      ...sharedFilters,
      granularity,
    })
  }

  return salesSummaryReportFiltersSchema.parse({
    ...parsedSearch,
    ...sharedFilters,
    granularity,
  })
}

export const floristPerformanceReportSearchSchema = buildOptionalDateRangeSearchSchema({
  view: floristPerformanceViewSchema.optional(),
  status: floristPerformanceStatusSchema.optional(),
  search: z.string().optional().catch(undefined),
  momentum_metric: floristPerformanceMomentumMetricSchema.optional(),
  sort: floristPerformanceSortSchema.optional(),
  ...reportPaginationSearchSchema,
})

export type FloristPerformanceReportSearch = z.infer<typeof floristPerformanceReportSearchSchema>

type FloristPerformanceView = z.infer<typeof floristPerformanceViewSchema>

export type FloristPerformanceReportFilters = {
  view: FloristPerformanceView
  start_date: string
  end_date: string
  status: z.infer<typeof floristPerformanceStatusSchema>
  search: string | null
  momentum_metric: z.infer<typeof floristPerformanceMomentumMetricSchema>
  sort: z.infer<typeof floristPerformanceSortSchema>
  page: number
  page_size: number
}

export function getDefaultFloristPerformanceReportFilters(
  now: Dayjs,
  view: FloristPerformanceView = FLORIST_PERFORMANCE_VIEW.ORDERS
): FloristPerformanceReportFilters {
  const endDate = view === FLORIST_PERFORMANCE_VIEW.MOMENTUM ? now.subtract(1, 'day') : now

  return {
    view,
    start_date:
      view === FLORIST_PERFORMANCE_VIEW.MOMENTUM
        ? endDate.format('YYYY-MM-DD')
        : endDate.subtract(29, 'day').format('YYYY-MM-DD'),
    end_date: endDate.format('YYYY-MM-DD'),
    status: FLORIST_PERFORMANCE_STATUS.ALL,
    search: null,
    momentum_metric: FLORIST_PERFORMANCE_MOMENTUM_METRIC.PLATFORM_REVENUE,
    sort:
      view === FLORIST_PERFORMANCE_VIEW.MOMENTUM
        ? FLORIST_PERFORMANCE_SORT.CURRENT_30_DESC
        : FLORIST_PERFORMANCE_SORT.TOTAL_DESC,
    page: 1,
    page_size: 20,
  }
}

export function getFloristPerformanceReportFiltersFromSearch(
  search: unknown,
  defaultFilters: FloristPerformanceReportFilters
): FloristPerformanceReportFilters {
  const parsed = floristPerformanceReportSearchSchema.parse(search)
  const view = parsed.view ?? defaultFilters.view
  const hasDates = hasExplicitDateRange(parsed)
  const endDate = hasDates ? parsed.end_date : defaultFilters.end_date

  return {
    view,
    start_date:
      view === FLORIST_PERFORMANCE_VIEW.MOMENTUM
        ? endDate
        : hasDates
          ? parsed.start_date
          : defaultFilters.start_date,
    end_date: endDate,
    status: parsed.status ?? defaultFilters.status,
    search: parsed.search?.trim() || null,
    momentum_metric: parsed.momentum_metric ?? defaultFilters.momentum_metric,
    sort:
      parsed.sort ??
      (view === FLORIST_PERFORMANCE_VIEW.MOMENTUM
        ? FLORIST_PERFORMANCE_SORT.CURRENT_30_DESC
        : FLORIST_PERFORMANCE_SORT.TOTAL_DESC),
    page: parsed.page ?? defaultFilters.page,
    page_size: parsed.page_size ?? parsed.pageSize ?? defaultFilters.page_size,
  }
}

export function getFloristPerformanceReportSearchParams(filters: FloristPerformanceReportFilters) {
  return {
    view: filters.view === FLORIST_PERFORMANCE_VIEW.ORDERS ? undefined : filters.view || undefined,
    start_date: filters.start_date,
    end_date: filters.end_date,
    status:
      filters.status === FLORIST_PERFORMANCE_STATUS.ALL ? undefined : filters.status || undefined,
    search: filters.search || undefined,
    momentum_metric:
      filters.momentum_metric === FLORIST_PERFORMANCE_MOMENTUM_METRIC.PLATFORM_REVENUE
        ? undefined
        : filters.momentum_metric || undefined,
    sort:
      filters.sort ===
      (filters.view === FLORIST_PERFORMANCE_VIEW.MOMENTUM
        ? FLORIST_PERFORMANCE_SORT.CURRENT_30_DESC
        : FLORIST_PERFORMANCE_SORT.TOTAL_DESC)
        ? undefined
        : filters.sort || undefined,
    page: filters.page > 1 ? filters.page : undefined,
    page_size: filters.page_size === 20 ? undefined : filters.page_size,
  }
}

export function getSalesSummaryGranularityOptionsForRange(range: ReportDateRange) {
  return SALES_SUMMARY_GRANULARITY_OPTIONS.map((option) => ({
    ...option,
    disabled: !isSalesSummaryGranularityAllowedForRange(option.value, range),
  }))
}

/** The granularity a group-by select value names, or undefined when it names none. */
export function parseSalesSummaryGranularity(value: string): SalesSummaryGranularity | undefined {
  const result = salesSummaryGranularitySchema.safeParse(value)

  return result.success ? result.data : undefined
}

export function resolveSalesSummaryGranularityForRange(
  granularity: SalesSummaryGranularity | undefined,
  range: ReportDateRange
): SalesSummaryGranularity {
  if (granularity && isSalesSummaryGranularityAllowedForRange(granularity, range)) {
    return granularity
  }

  return getDefaultSalesSummaryGranularityForRange(range)
}

function getDefaultSalesSummaryGranularityForRange(range: ReportDateRange) {
  const selectedDayCount = getSalesSummarySelectedDayCount(range)

  if (selectedDayCount === 1) {
    return SALES_SUMMARY_GRANULARITY.HOURLY
  }

  if (selectedDayCount <= 120) {
    return SALES_SUMMARY_GRANULARITY.DAILY
  }

  return selectedDayCount <= 365
    ? SALES_SUMMARY_GRANULARITY.WEEKLY
    : SALES_SUMMARY_GRANULARITY.MONTHLY
}

function isSalesSummaryGranularityAllowedForRange(
  granularity: SalesSummaryGranularity,
  range: ReportDateRange
) {
  const selectedDayCount = getSalesSummarySelectedDayCount(range)

  switch (granularity) {
    case SALES_SUMMARY_GRANULARITY.HOURLY:
      return selectedDayCount <= SALES_SUMMARY_HOURLY_MAX_DAYS
    case SALES_SUMMARY_GRANULARITY.DAILY:
      return selectedDayCount <= SALES_SUMMARY_DAILY_MAX_DAYS
    case SALES_SUMMARY_GRANULARITY.WEEKLY:
      return selectedDayCount <= SALES_SUMMARY_WEEKLY_MAX_DAYS
    case SALES_SUMMARY_GRANULARITY.MONTHLY:
      return true
    default:
      return false
  }
}

function getSalesSummarySelectedDayCount({ start_date, end_date }: ReportDateRange = {}) {
  if (!start_date || !end_date) {
    return 1
  }

  const startDate = getUtcDateValue(start_date)
  const endDate = getUtcDateValue(end_date)
  const dayLength = 24 * 60 * 60 * 1000
  const difference = Math.round((endDate - startDate) / dayLength)

  return Math.max(1, difference + 1)
}

function getUtcDateValue(value: string) {
  // A missing part becomes NaN, as `undefined` would in the date math below.
  const [year = NaN, month = NaN, day = NaN] = value.split('-').map(Number)

  return Date.UTC(year, month - 1, day)
}

export const tipsReportSortValues = ['newest', 'highest_tip', 'oldest', 'source_pos_first'] as const

export const tipsReportFiltersSchema = buildRequiredDateRangeSchema({
  date_mode: reportDateModeSchema.optional(),
  sort_by: z.enum(tipsReportSortValues).default('newest'),
})

export const tipsReportViewValues = ['orders', 'people'] as const

export const tipsReportSearchSchema = buildOptionalDateRangeSearchSchema({
  date_mode: reportDateModeSchema.optional(),
  sort_by: z.enum(tipsReportSortValues).optional(),
  // Which tab the page shows; one report answers both, so it isn't a report filter.
  view: z.enum(tipsReportViewValues).optional(),
})

export type TipsReportFilters = z.infer<typeof tipsReportFiltersSchema>
export type TipsReportSearch = z.infer<typeof tipsReportSearchSchema>
export type TipsReportView = (typeof tipsReportViewValues)[number]

// Callers pass a date range; sort_by falls back to the schema default.
type TipsReportDefaultFilters = z.input<typeof tipsReportFiltersSchema>

export function getDefaultTipsReportFilters(
  defaultFilters: TipsReportDefaultFilters = getCurrentReportDateFilters()
): TipsReportFilters {
  return tipsReportFiltersSchema.parse({
    sort_by: 'newest',
    ...defaultFilters,
  })
}

export function getTipsReportFiltersFromSearch(
  search: unknown,
  defaultFilters: TipsReportDefaultFilters
): TipsReportFilters {
  const parsedSearch = tipsReportSearchSchema.parse(search)
  const sharedFilters = {
    date_mode: parsedSearch.date_mode ?? defaultFilters?.date_mode ?? REPORT_DATE_MODE.CUSTOM,
    sort_by: parsedSearch.sort_by ?? 'newest',
  }

  if (!hasExplicitDateRange(parsedSearch)) {
    return getDefaultTipsReportFilters({
      ...defaultFilters,
      ...sharedFilters,
    })
  }

  return tipsReportFiltersSchema.parse({
    ...parsedSearch,
    ...sharedFilters,
  })
}

const externalOrdersSourceSystemSchema = z.enum(['fsn_network', 'condolences'])

export const externalOrdersReportFiltersSchema = buildRequiredDateRangeSchema({
  date_mode: reportDateModeSchema.optional(),
  source_system: externalOrdersSourceSystemSchema.nullable().optional(),
})

export const externalOrdersReportSearchSchema = buildOptionalDateRangeSearchSchema({
  date_mode: reportDateModeSchema.optional(),
  source_system: externalOrdersSourceSystemSchema.optional(),
})

export type ExternalOrdersReportFilters = z.infer<typeof externalOrdersReportFiltersSchema>
export type ExternalOrdersReportSearch = z.infer<typeof externalOrdersReportSearchSchema>

export function getExternalOrdersReportFiltersFromSearch(
  search: unknown,
  defaultFilters: ExternalOrdersReportFilters
): ExternalOrdersReportFilters {
  const parsedSearch = externalOrdersReportSearchSchema.parse(search)
  const sharedFilters = {
    date_mode: parsedSearch.date_mode ?? defaultFilters?.date_mode ?? REPORT_DATE_MODE.CUSTOM,
    source_system: parsedSearch.source_system ?? null,
  }

  if (!hasExplicitDateRange(parsedSearch)) {
    return externalOrdersReportFiltersSchema.parse({
      ...defaultFilters,
      ...sharedFilters,
    })
  }

  return externalOrdersReportFiltersSchema.parse({
    ...parsedSearch,
    ...sharedFilters,
  })
}

export const wiredOrdersReportSearchSchema = buildOptionalDateRangeSearchSchema({
  date_mode: reportDateModeSchema.optional(),
})

export type WiredOrdersReportSearch = z.infer<typeof wiredOrdersReportSearchSchema>

/** The Florist Map starts on live shops alone. */
export const DEFAULT_FLORIST_MAP_STATUSES: readonly FloristStatusFilter[] = ['live']

export const floristMapSearchSchema = z.object({
  status: z.array(floristStatusSchema).optional().catch(undefined),
  // Coerced: the router reads a link's `?search=247` (or `true`) as JSON, not as the text typed.
  search: z.coerce.string().optional().catch(undefined),
})
